/**
 * SSGI Programs catalog.
 * A single source of truth used by /programs (index) and /programs/:slug (detail).
 * Adjust freely — pages are rendered directly from this data.
 */

const CAREER_MAP = {
  cse: ["Software Engineer", "Full-Stack Developer", "Data Analyst", "AI/ML Engineer", "Cloud Engineer", "DevOps Engineer", "Cyber Security Analyst"],
  ece: ["Embedded Systems Engineer", "VLSI Design Engineer", "Telecom Engineer", "IoT Engineer", "Semiconductor R&D", "Networks Engineer"],
  mech: ["Design Engineer", "Production Engineer", "Quality Engineer", "R&D Engineer", "Maintenance Engineer", "Automobile Engineer"],
  civil: ["Site / Project Engineer", "Structural Engineer", "Transportation Engineer", "Environmental Engineer", "Public-Works Officer", "Construction Manager"],
  ee: ["Power Systems Engineer", "Electrical Design Engineer", "Renewable Energy Engineer", "Control Engineer", "Substation Engineer"],
  mgmt: ["Business Analyst", "Marketing Manager", "HR Manager", "Operations Manager", "Financial Analyst", "Entrepreneur"],
  it: ["Software Developer", "Database Administrator", "Network Engineer", "System Analyst", "Web Developer", "Cyber Security Analyst"],
  pharma: ["Drug Inspector", "Medical Representative", "Pharmacist", "R&D Chemist", "Clinical Research Associate", "Regulatory Affairs Officer"],
  education: ["School Teacher", "Educational Administrator", "Curriculum Designer", "Academic Counsellor", "Trainer", "Educational Researcher"],
  agri: ["Agricultural Officer", "Farm Manager", "Agri-Business Manager", "Extension Officer", "Seed Technologist", "Agri-Preneur"],
  hotel: ["Restaurant Manager", "Front-Office Manager", "F&B Manager", "Executive Chef", "Housekeeping Manager", "Cruise / Hotelier"],
  applied: ["Research Associate", "Lab Analyst", "School / College Lecturer", "Data Scientist", "R&D Scientist", "Quality Analyst"],
  diploma: ["Junior Engineer", "Site Supervisor", "Technician", "CAD / Design Assistant", "Lateral entry to B.Tech (2nd year)"],
};

const RECRUITERS_BY_STREAM = {
  tech: ["TCS", "Infosys", "Wipro", "HCL", "Cognizant", "Amazon", "Accenture", "IBM", "Deloitte", "Capgemini"],
  core: ["L&T", "Hero MotoCorp", "TVS Motors", "Tata Steel", "Ashok Leyland", "Bosch", "Mahindra", "JCB"],
  mgmt: ["HDFC Bank", "ICICI Bank", "Axis Bank", "Kotak Mahindra", "Nestlé", "Reliance Retail", "Bajaj", "Deloitte"],
  pharma: ["Cipla", "Sun Pharma", "Torrent Pharma", "Alkem Labs", "Zydus Cadila", "Mankind Pharma", "Dr. Reddy's", "Lupin"],
  edu: ["DPS", "Delhi Public World School", "Ryan International", "Cambridge International", "Kendriya Vidyalayas (through CTET)", "State Govt. schools"],
  agri: ["ITC Agri Business", "Godrej Agrovet", "Rallis India", "Bayer CropScience", "Coromandel Fertilisers", "Punjab Agri Board"],
  hotel: ["Taj Hotels", "The Oberoi", "ITC Hotels", "Marriott", "Radisson", "Hyatt", "Cruise lines"],
};

/**
 * Program list. `slug` drives the URL: /programs/:slug
 */
export const programs = [
  /* ============ ENGINEERING ============ */
  {
    slug: "btech-cse",
    name: "B.Tech in Computer Science & Engineering",
    short: "B.Tech CSE",
    discipline: "Engineering",
    degree: "Bachelor's Degree",
    duration: "4 Years (8 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 120,
    fee: "₹78,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "10+2 with Physics, Mathematics and one of Chemistry / Biology / Biotechnology / Computer Science / Technical Vocational Subject — minimum 45% marks (40% for reserved categories).",
    highlights: [
      "Curriculum aligned with IBM CoE & Pearson VUE certifications",
      "Elective tracks in AI/ML, Data Science, Cyber Security & Cloud",
      "Hands-on labs: Full-stack, Cloud (AWS/Azure), DevOps, IoT",
      "Career Udaan Placement Assurance Programme from Year 1",
      "Industrial training, capstone project and internships",
    ],
    semesters: [
      "Sem 1-2: Programming, Maths, Digital Logic, Communication",
      "Sem 3-4: DSA, OOP, DBMS, Operating Systems, Computer Networks",
      "Sem 5-6: Software Engineering, Web Tech, AI, Compiler Design",
      "Sem 7-8: Electives (ML, Cloud, Cyber Sec), Capstone, Internship",
    ],
    careers: CAREER_MAP.cse,
    recruiters: RECRUITERS_BY_STREAM.tech,
    tags: ["Popular", "IBM Track", "Highest ROI"],
  },
  {
    slug: "btech-ece",
    name: "B.Tech in Electronics & Communication Engineering",
    short: "B.Tech ECE",
    discipline: "Engineering",
    degree: "Bachelor's Degree",
    duration: "4 Years (8 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹75,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "10+2 with Physics, Mathematics and one of Chemistry / Biology / Biotechnology / Computer Science — minimum 45% marks (40% for reserved categories).",
    highlights: [
      "State-of-the-art VLSI, DSP, Communication & Embedded labs",
      "Hands-on with Cadence, Xilinx, MATLAB and PCB design",
      "Emerging tracks in IoT, 5G, Antenna Design and Robotics",
      "MoUs with semiconductor and telecom industry",
    ],
    semesters: [
      "Sem 1-2: Applied Physics, Maths, Circuits, C-programming",
      "Sem 3-4: Analog / Digital Electronics, Signals & Systems",
      "Sem 5-6: Communication Systems, VLSI, DSP, Microcontrollers",
      "Sem 7-8: IoT, Wireless Networks, Electives, Project",
    ],
    careers: CAREER_MAP.ece,
    recruiters: [...RECRUITERS_BY_STREAM.tech, "Bharat Electronics", "ISRO (through GATE)"],
    tags: ["Core Branch"],
  },
  {
    slug: "btech-mech",
    name: "B.Tech in Mechanical Engineering",
    short: "B.Tech Mechanical",
    discipline: "Engineering",
    degree: "Bachelor's Degree",
    duration: "4 Years (8 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹75,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "10+2 with Physics, Mathematics and one of Chemistry / Biology / Biotechnology / Computer Science — minimum 45% marks (40% for reserved categories).",
    highlights: [
      "Modern CAD/CAM/CAE labs with SolidWorks, ANSYS, CATIA",
      "Workshop with CNC, 3D printing and robotics kits",
      "Industry-integrated tracks: EV, Automation, Manufacturing",
      "Regular MoUs with Hero MotoCorp, JCB, Mahindra",
    ],
    semesters: [
      "Sem 1-2: Engg Graphics, Maths, Physics, Workshop",
      "Sem 3-4: Thermodynamics, SoM, Fluid Mechanics",
      "Sem 5-6: Machine Design, IC Engines, HMT, Manufacturing",
      "Sem 7-8: Robotics, CAD/CAM, Electives, Major Project",
    ],
    careers: CAREER_MAP.mech,
    recruiters: RECRUITERS_BY_STREAM.core,
    tags: ["Core Branch"],
  },
  {
    slug: "btech-civil",
    name: "B.Tech in Civil Engineering",
    short: "B.Tech Civil",
    discipline: "Engineering",
    degree: "Bachelor's Degree",
    duration: "4 Years (8 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹75,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "10+2 with Physics, Mathematics and one of Chemistry / Biology / Biotechnology / Computer Science — minimum 45% marks (40% for reserved categories).",
    highlights: [
      "Well-equipped Surveying, Concrete, Geo-tech and Env. labs",
      "Software exposure: AutoCAD, STAAD.Pro, ETABS, Revit BIM",
      "Field trips to major infrastructure projects",
      "Placements with L&T, Shapoorji, NHAI, PWD & real-estate majors",
    ],
    semesters: [
      "Sem 1-2: Engg Mechanics, Building Materials, Surveying",
      "Sem 3-4: RCC, Fluid Mech, Geo-tech, Transportation",
      "Sem 5-6: Structural Analysis, Environmental Engg, Steel Design",
      "Sem 7-8: Project Management, Estimation, Electives, Project",
    ],
    careers: CAREER_MAP.civil,
    recruiters: ["L&T", "Shapoorji Pallonji", "NHAI", "PWD Punjab", "DLF", "GMR"],
    tags: ["Core Branch"],
  },
  {
    slug: "btech-electrical",
    name: "B.Tech in Electrical Engineering",
    short: "B.Tech Electrical",
    discipline: "Engineering",
    degree: "Bachelor's Degree",
    duration: "4 Years (8 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹75,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "10+2 with Physics, Mathematics and one of Chemistry / Biology / Biotechnology / Computer Science — minimum 45% marks (40% for reserved categories).",
    highlights: [
      "Power Systems, Machines, Control & Renewable Energy labs",
      "Hands-on with PLC / SCADA, MATLAB / Simulink",
      "EV, Solar and Smart-Grid electives",
      "Placements in PSPCL, BHEL, Tata Power, Adani Power etc.",
    ],
    semesters: [
      "Sem 1-2: Engg Physics, Circuits, Programming",
      "Sem 3-4: Machines, Power Electronics, Control Systems",
      "Sem 5-6: Power Systems, Utilization, Renewable Energy",
      "Sem 7-8: EV Tech, Smart Grid, Electives, Project",
    ],
    careers: CAREER_MAP.ee,
    recruiters: ["BHEL", "Tata Power", "Adani Power", "PSPCL", "Siemens", "Schneider Electric"],
    tags: ["Core Branch"],
  },
  {
    slug: "mtech-cse",
    name: "M.Tech in Computer Science & Engineering",
    short: "M.Tech CSE",
    discipline: "Engineering",
    degree: "Master's Degree",
    duration: "2 Years (4 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 18,
    fee: "₹65,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "B.Tech / B.E. in CSE / IT or MCA with minimum 50% marks (45% for reserved categories). Valid GATE score preferred.",
    highlights: [
      "Advanced electives in AI/ML, Cloud, Cyber Security, Big Data",
      "Research thesis + publications encouraged (Scopus, IEEE)",
      "Assistantship options for GATE-qualified candidates",
      "PhD-track pathway",
    ],
    semesters: ["Sem 1-2: Adv. Algorithms, ML, Adv. DBMS, Research Methodology", "Sem 3-4: Electives + Dissertation"],
    careers: ["Senior Software Engineer", "Data Scientist", "AI Researcher", "Cloud Architect", "Academician"],
    recruiters: RECRUITERS_BY_STREAM.tech,
    tags: ["Postgraduate", "Research"],
  },
  {
    slug: "mtech-ece",
    name: "M.Tech in Electronics & Communication",
    short: "M.Tech ECE",
    discipline: "Engineering",
    degree: "Master's Degree",
    duration: "2 Years (4 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 18,
    fee: "₹65,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "B.Tech / B.E. in ECE / EE / EIE with minimum 50% marks (45% for reserved). Valid GATE score preferred.",
    highlights: ["VLSI, DSP, Wireless Communication specialisations", "Access to premium research tools", "Dissertation with industry mentors"],
    semesters: ["Sem 1-2: Adv. Digital Systems, DSP, Wireless Comm.", "Sem 3-4: Electives + Dissertation"],
    careers: ["VLSI Design Engineer", "Wireless Systems Engineer", "R&D Scientist", "Academician"],
    recruiters: RECRUITERS_BY_STREAM.tech,
    tags: ["Postgraduate"],
  },
  {
    slug: "mtech-mech",
    name: "M.Tech in Mechanical Engineering",
    short: "M.Tech Mechanical",
    discipline: "Engineering",
    degree: "Master's Degree",
    duration: "2 Years (4 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 18,
    fee: "₹65,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "B.Tech / B.E. in Mechanical / Production / Automobile — minimum 50% (45% reserved). Valid GATE preferred.",
    highlights: ["Specialisations: Thermal, Manufacturing, Design", "Access to industry-grade simulation labs", "Publication & patent support"],
    semesters: ["Sem 1-2: Adv. Manufacturing, CAD/CAM, FEM", "Sem 3-4: Electives + Dissertation"],
    careers: ["Design / R&D Engineer", "Manufacturing Lead", "Academician"],
    recruiters: RECRUITERS_BY_STREAM.core,
    tags: ["Postgraduate"],
  },
  {
    slug: "mtech-civil-structural",
    name: "M.Tech in Civil (Structural Engineering)",
    short: "M.Tech Civil (Structural)",
    discipline: "Engineering",
    degree: "Master's Degree",
    duration: "2 Years (4 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 18,
    fee: "₹65,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "B.Tech / B.E. in Civil with minimum 50% (45% reserved). Valid GATE preferred.",
    highlights: ["Advanced concrete, steel & earthquake engg", "Software: STAAD.Pro, ETABS, SAP2000, Revit", "Live industry projects"],
    semesters: ["Sem 1-2: Adv. Structural Analysis, Concrete, Steel", "Sem 3-4: Earthquake Engg + Dissertation"],
    careers: ["Structural Consultant", "Design Engineer", "Site Manager", "Academician"],
    recruiters: ["L&T", "Shapoorji", "AECOM", "NHAI", "GMR"],
    tags: ["Postgraduate"],
  },
  {
    slug: "mtech-ee-power",
    name: "M.Tech in Electrical (Power Systems)",
    short: "M.Tech EE (Power)",
    discipline: "Engineering",
    degree: "Master's Degree",
    duration: "2 Years (4 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 18,
    fee: "₹65,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "B.Tech / B.E. in EE / EEE / EIE with minimum 50% (45% reserved). Valid GATE preferred.",
    highlights: ["Focus on smart grid, renewable & HV systems", "Simulation labs: MATLAB, ETAP, PSCAD", "Dissertation with industry / R&D bodies"],
    semesters: ["Sem 1-2: Adv. Power Systems, HVDC, Smart Grid", "Sem 3-4: Electives + Dissertation"],
    careers: ["Power Systems Engineer", "Grid Analyst", "Renewable Energy Consultant", "Academician"],
    recruiters: ["BHEL", "Tata Power", "Adani Power", "PGCIL", "Siemens"],
    tags: ["Postgraduate"],
  },

  /* ============ MANAGEMENT ============ */
  {
    slug: "mba",
    name: "Master of Business Administration (MBA)",
    short: "MBA",
    discipline: "Management",
    degree: "Master's Degree",
    duration: "2 Years (4 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 120,
    fee: "₹95,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "Bachelor's degree in any discipline with minimum 50% marks (45% reserved). CAT / MAT / CMAT / State CET score accepted.",
    highlights: [
      "Dual specialisations — Marketing, Finance, HR, Ops, IT, Business Analytics",
      "Live projects, case studies and industry immersion",
      "Guest lectures by CEOs / senior leaders",
      "Placement Assurance via Career Udaan",
    ],
    semesters: ["Sem 1-2: Core: Marketing, Finance, HR, Ops, Strategy", "Sem 3-4: Specialisations + Summer Internship + Live Project"],
    careers: CAREER_MAP.mgmt,
    recruiters: RECRUITERS_BY_STREAM.mgmt,
    tags: ["Flagship", "Popular"],
  },
  {
    slug: "bba",
    name: "Bachelor of Business Administration (BBA)",
    short: "BBA",
    discipline: "Management",
    degree: "Bachelor's Degree",
    duration: "3 Years (6 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹55,000 / year (approx)",
    approvedBy: "Affiliated to IKGPTU / Panjab University",
    eligibility: "10+2 in any stream with minimum 45% marks (40% reserved).",
    highlights: ["Early exposure to Marketing, HR, Finance & Analytics", "Communication, Aptitude & Personality Development", "Pathway to MBA / entrepreneurship"],
    semesters: ["Yr 1: Fundamentals of Mgmt, Accounting, Economics", "Yr 2: Marketing, HR, Finance, Ops", "Yr 3: Electives + Internship"],
    careers: ["Junior Marketing Executive", "Sales Executive", "HR Executive", "Business Analyst Trainee", "Entrepreneur"],
    recruiters: RECRUITERS_BY_STREAM.mgmt,
    tags: [],
  },
  {
    slug: "bcom-prof",
    name: "B.Com (Professional)",
    short: "B.Com (Prof)",
    discipline: "Management",
    degree: "Bachelor's Degree",
    duration: "3 Years (6 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹35,000 / year (approx)",
    approvedBy: "Affiliated to Panjab University",
    eligibility: "10+2 in any stream with minimum 45% marks. Preference to Commerce stream.",
    highlights: ["Prep track for CA / CS / CMA / CFA", "Tally, GST & MS-Excel practical modules", "Banking & Financial Services specialisation"],
    semesters: ["Yr 1: Financial Accounting, Business Economics", "Yr 2: Corporate Accounting, Cost, Auditing", "Yr 3: Taxation, Financial Mgmt, Electives"],
    careers: ["Accountant", "Tax Consultant", "Banking Officer", "CA / CS / CMA Aspirant", "Financial Analyst"],
    recruiters: ["HDFC", "ICICI", "Deloitte", "KPMG", "EY", "PwC"],
    tags: [],
  },

  /* ============ COMPUTING / IT ============ */
  {
    slug: "bca",
    name: "Bachelor of Computer Applications (BCA)",
    short: "BCA",
    discipline: "Computing / IT",
    degree: "Bachelor's Degree",
    duration: "3 Years (6 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹45,000 / year (approx)",
    approvedBy: "Affiliated to IKGPTU / Panjab University",
    eligibility: "10+2 in any stream (Maths preferred) with minimum 45% marks.",
    highlights: ["Focused on programming, DBMS, Web, Cloud", "Industry certifications via Pearson VUE", "Direct pathway to MCA"],
    semesters: ["Yr 1: Programming (C/C++), DBMS, OS Basics", "Yr 2: Java, Python, DSA, Web Dev", "Yr 3: Cloud, AI, Cyber Sec, Project"],
    careers: CAREER_MAP.it,
    recruiters: RECRUITERS_BY_STREAM.tech,
    tags: ["Popular"],
  },
  {
    slug: "mca",
    name: "Master of Computer Applications (MCA)",
    short: "MCA",
    discipline: "Computing / IT",
    degree: "Master's Degree",
    duration: "2 Years (4 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹70,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "BCA / B.Sc IT / B.Sc CS / B.Tech with minimum 50% marks (45% reserved) and Maths at 10+2 or graduation.",
    highlights: ["Full-Stack, ML, Cloud & Cyber Sec electives", "Industry-integrated Capstone Project", "IBM CoE & Pearson VUE testing centre"],
    semesters: ["Sem 1-2: Adv. Programming, DBMS, OS, Networks", "Sem 3-4: Electives + Major Project + Internship"],
    careers: CAREER_MAP.cse,
    recruiters: RECRUITERS_BY_STREAM.tech,
    tags: ["Postgraduate", "Popular"],
  },
  {
    slug: "bsc-it",
    name: "B.Sc in Information Technology",
    short: "B.Sc IT",
    discipline: "Computing / IT",
    degree: "Bachelor's Degree",
    duration: "3 Years (6 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹40,000 / year (approx)",
    approvedBy: "Affiliated to Panjab University",
    eligibility: "10+2 (Science with Maths / Computer Science) with minimum 45% marks.",
    highlights: ["Balanced theory + practical labs", "Programming, DBMS, Web, Cyber Sec", "Direct pathway to MCA / M.Sc IT"],
    semesters: ["Yr 1: Programming, Maths, Digital Fundamentals", "Yr 2: DBMS, OS, Web", "Yr 3: Electives + Project"],
    careers: CAREER_MAP.it,
    recruiters: RECRUITERS_BY_STREAM.tech,
    tags: [],
  },
  {
    slug: "msc-it",
    name: "M.Sc in Information Technology",
    short: "M.Sc IT",
    discipline: "Computing / IT",
    degree: "Master's Degree",
    duration: "2 Years (4 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 30,
    fee: "₹55,000 / year (approx)",
    approvedBy: "Affiliated to Panjab University",
    eligibility: "B.Sc IT / BCA / B.Tech / B.Sc (CS) with minimum 50% marks (45% reserved).",
    highlights: ["Focus on advanced computing & analytics", "Dissertation / Live project", "Assistantship possibilities for research"],
    semesters: ["Sem 1-2: Adv. DBMS, Software Engg, Networks", "Sem 3-4: Electives + Dissertation"],
    careers: CAREER_MAP.it,
    recruiters: RECRUITERS_BY_STREAM.tech,
    tags: ["Postgraduate"],
  },

  /* ============ PHARMACY ============ */
  {
    slug: "d-pharma",
    name: "Diploma in Pharmacy (D.Pharma)",
    short: "D.Pharma",
    discipline: "Pharmacy",
    degree: "Diploma",
    duration: "2 Years",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹50,000 / year (approx)",
    approvedBy: "Pharmacy Council of India (PCI) · PSBTE",
    eligibility: "10+2 with Physics, Chemistry and Biology / Maths with minimum 50% marks (45% reserved).",
    highlights: ["Direct pathway to registered Pharmacist licence", "Hands-on dispensing & compounding labs", "Career in retail / hospital pharmacy"],
    semesters: ["Yr 1: Pharmaceutics, Pharm. Chemistry, Human Anatomy", "Yr 2: Pharmacology, Pharmacognosy, Practical training"],
    careers: CAREER_MAP.pharma,
    recruiters: RECRUITERS_BY_STREAM.pharma,
    tags: [],
  },
  {
    slug: "b-pharma",
    name: "Bachelor of Pharmacy (B.Pharma)",
    short: "B.Pharma",
    discipline: "Pharmacy",
    degree: "Bachelor's Degree",
    duration: "4 Years (8 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 100,
    fee: "₹85,000 / year (approx)",
    approvedBy: "Pharmacy Council of India (PCI) · Affiliated to IKGPTU",
    eligibility: "10+2 with Physics, Chemistry and Biology / Maths with minimum 50% marks (45% reserved).",
    highlights: ["Extensive lab exposure & industrial training", "PCI-approved curriculum with GPAT / NIPER prep", "Placements in leading pharma companies"],
    semesters: ["Sem 1-4: Pharmaceutics, Pharm. Chemistry, Pharmacology, Pharmacognosy", "Sem 5-8: Advanced Therapeutics, Regulatory Affairs, Project"],
    careers: CAREER_MAP.pharma,
    recruiters: RECRUITERS_BY_STREAM.pharma,
    tags: ["Popular"],
  },
  {
    slug: "b-pharma-practice",
    name: "B.Pharma (Practice) — Lateral / Post-D.Pharma",
    short: "B.Pharma (Practice)",
    discipline: "Pharmacy",
    degree: "Bachelor's Degree",
    duration: "2 Years (Lateral Entry)",
    mode: "Full-Time · On-Campus",
    seats: 30,
    fee: "₹85,000 / year (approx)",
    approvedBy: "Pharmacy Council of India (PCI)",
    eligibility: "D.Pharma with minimum 55% marks and PCI registration.",
    highlights: ["Fast-track B.Pharma for existing D.Pharma diploma holders", "Direct entry in 2nd year (3rd semester)"],
    semesters: ["Sem 3-6: Advanced Pharmaceutics, Pharmacology, Clinical Pharmacy, Project"],
    careers: CAREER_MAP.pharma,
    recruiters: RECRUITERS_BY_STREAM.pharma,
    tags: ["Lateral Entry"],
  },
  {
    slug: "m-pharma",
    name: "Master of Pharmacy (M.Pharma)",
    short: "M.Pharma",
    discipline: "Pharmacy",
    degree: "Master's Degree",
    duration: "2 Years (4 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 24,
    fee: "₹90,000 / year (approx)",
    approvedBy: "Pharmacy Council of India (PCI) · Affiliated to IKGPTU",
    eligibility: "B.Pharma with minimum 55% marks (50% reserved). Valid GPAT preferred.",
    highlights: ["Specialisations: Pharmaceutics, Pharmacology", "Research & dissertation with publications", "GPAT-qualified stipend available"],
    semesters: ["Sem 1-2: Adv. Pharmaceutics / Pharmacology, Research Methodology", "Sem 3-4: Dissertation + Seminars"],
    careers: ["R&D Scientist", "Regulatory Affairs Officer", "Clinical Research Associate", "Academician"],
    recruiters: RECRUITERS_BY_STREAM.pharma,
    tags: ["Postgraduate", "Research"],
  },

  /* ============ EDUCATION ============ */
  {
    slug: "b-ed",
    name: "Bachelor of Education (B.Ed)",
    short: "B.Ed",
    discipline: "Education",
    degree: "Bachelor's Degree",
    duration: "2 Years",
    mode: "Full-Time · On-Campus",
    seats: 100,
    fee: "₹42,000 / year (approx)",
    approvedBy: "NCTE · Affiliated to affiliating University",
    eligibility: "Bachelor's degree with minimum 50% marks (45% reserved).",
    highlights: ["ICT-integrated pedagogy training", "Internship in reputed schools", "CTET / STET preparation support"],
    semesters: ["Yr 1: Foundations of Education, Learning & Development", "Yr 2: Pedagogy Practicals, School Internship, Project"],
    careers: CAREER_MAP.education,
    recruiters: RECRUITERS_BY_STREAM.edu,
    tags: [],
  },
  {
    slug: "m-ed",
    name: "Master of Education (M.Ed)",
    short: "M.Ed",
    discipline: "Education",
    degree: "Master's Degree",
    duration: "2 Years",
    mode: "Full-Time · On-Campus",
    seats: 35,
    fee: "₹45,000 / year (approx)",
    approvedBy: "NCTE · Affiliated to affiliating University",
    eligibility: "B.Ed with minimum 55% marks (50% reserved).",
    highlights: ["Focus on educational leadership & research", "Publication & thesis support", "Pathway to NET-JRF / PhD"],
    semesters: ["Sem 1-2: Adv. Educational Psychology, Research Methods", "Sem 3-4: Dissertation + Advanced Electives"],
    careers: ["Educational Researcher", "Curriculum Designer", "College Lecturer", "Academic Administrator"],
    recruiters: RECRUITERS_BY_STREAM.edu,
    tags: ["Postgraduate"],
  },
  {
    slug: "d-el-ed",
    name: "Diploma in Elementary Education (D.El.Ed / ETT)",
    short: "D.El.Ed / ETT",
    discipline: "Education",
    degree: "Diploma",
    duration: "2 Years",
    mode: "Full-Time · On-Campus",
    seats: 50,
    fee: "₹30,000 / year (approx)",
    approvedBy: "NCTE · PSBTE",
    eligibility: "10+2 with minimum 50% marks (45% reserved).",
    highlights: ["Prepare for primary teaching (Classes 1–5)", "CTET Paper-1 preparation support", "Focused pedagogy internship"],
    semesters: ["Yr 1: Child Development, Language / Maths pedagogy", "Yr 2: EVS Pedagogy, School Internship, Assessment"],
    careers: ["Primary School Teacher", "Anganwadi Supervisor", "Educational Assistant"],
    recruiters: RECRUITERS_BY_STREAM.edu,
    tags: [],
  },

  /* ============ AGRICULTURE ============ */
  {
    slug: "bsc-agri",
    name: "B.Sc (Honours) in Agriculture",
    short: "B.Sc Agriculture",
    discipline: "Agriculture",
    degree: "Bachelor's Degree (Honours)",
    duration: "4 Years (8 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹70,000 / year (approx)",
    approvedBy: "ICAR-aligned · Affiliated to affiliating University",
    eligibility: "10+2 with Physics, Chemistry, Biology / Maths / Agriculture with minimum 50% marks (45% reserved).",
    highlights: ["Field visits to research farms & Krishi Vigyan Kendras", "RAWE (Rural Agri Work Experience) programme", "Preparation for IBPS-AFO, NABARD, ICAR exams"],
    semesters: ["Yr 1: Fundamentals of Crop Sciences, Soil, Horticulture", "Yr 2-3: Agronomy, Entomology, Genetics", "Yr 4: RAWE + Project + Electives"],
    careers: CAREER_MAP.agri,
    recruiters: RECRUITERS_BY_STREAM.agri,
    tags: [],
  },

  /* ============ ALLIED HEALTH SCIENCES ============ */
  {
    slug: "bsc-radiology",
    name: "B.Sc in Radiology & Imaging Technology",
    short: "B.Sc Radiology",
    discipline: "Allied Health Sciences",
    degree: "Bachelor's Degree",
    duration: "3 Years + 6 Months Internship",
    mode: "Full-Time · On-Campus",
    seats: 40,
    fee: "₹65,000 / year (approx)",
    approvedBy: "Affiliated to Panjab University · Recognised by paramedical councils",
    eligibility: "10+2 with Physics, Chemistry and Biology / Maths with minimum 50% marks (45% reserved).",
    highlights: [
      "Hands-on training on X-Ray, CT, MRI and USG modalities",
      "Clinical postings at reputed multi-speciality hospitals",
      "6-month mandatory internship in the final year",
      "Placements in leading hospitals & diagnostic chains",
    ],
    semesters: [
      "Yr 1: Human Anatomy, Physiology, Radiation Physics",
      "Yr 2: Radiographic Techniques, Contrast Studies, CT & MRI Basics",
      "Yr 3: Advanced Imaging, PACS, Interventional Radiology",
      "Internship: 6 months in an approved hospital",
    ],
    careers: ["Radiographer", "CT/MRI Technologist", "Sonographer Assistant", "Cath-Lab Technician", "Imaging Sales", "Academician"],
    recruiters: ["Fortis", "Max Healthcare", "Apollo", "Manipal Hospitals", "Dr. Lal PathLabs", "SRL Diagnostics"],
    tags: ["Healthcare"],
  },
  {
    slug: "bsc-ott",
    name: "B.Sc in Operation Theatre Technology (OTT)",
    short: "B.Sc OTT",
    discipline: "Allied Health Sciences",
    degree: "Bachelor's Degree",
    duration: "3 Years + 6 Months Internship",
    mode: "Full-Time · On-Campus",
    seats: 40,
    fee: "₹65,000 / year (approx)",
    approvedBy: "Affiliated to Panjab University · Recognised by paramedical councils",
    eligibility: "10+2 with Physics, Chemistry and Biology / Maths with minimum 50% marks (45% reserved).",
    highlights: [
      "Extensive OT-based practical exposure at partner hospitals",
      "Training in surgical assistance, sterilisation and anaesthesia support",
      "Career-ready OT Technician certification pathway",
      "In-demand roles at major hospitals",
    ],
    semesters: [
      "Yr 1: Anatomy, Physiology, Microbiology, Basics of OT",
      "Yr 2: Anaesthesia Tech, Sterilisation, Surgical Instruments",
      "Yr 3: OT Management, Emergency & Critical Care Support",
      "Internship: 6 months in an approved hospital",
    ],
    careers: ["OT Technician", "Anaesthesia Technician", "CSSD Technician", "Cath-Lab Assistant", "ICU Support Technician"],
    recruiters: ["Fortis", "Max Healthcare", "Apollo", "Manipal Hospitals", "Medanta", "Local multi-speciality hospitals"],
    tags: ["Healthcare"],
  },
  {
    slug: "bmls",
    name: "Bachelor of Medical Laboratory Sciences (BMLS)",
    short: "BMLS",
    discipline: "Allied Health Sciences",
    degree: "Bachelor's Degree",
    duration: "3 Years + 6 Months Internship",
    mode: "Full-Time · On-Campus",
    seats: 40,
    fee: "₹65,000 / year (approx)",
    approvedBy: "Affiliated to Panjab University · Recognised by paramedical councils",
    eligibility: "10+2 with Physics, Chemistry and Biology with minimum 50% marks (45% reserved).",
    highlights: [
      "Modern medical lab, haematology and pathology facilities",
      "Training on biochemistry analysers, cell counters, microscopy",
      "Clinical postings at hospitals and diagnostic labs",
      "Growing demand for skilled MLT professionals",
    ],
    semesters: [
      "Yr 1: Human Anatomy, Physiology, Basic Biochemistry",
      "Yr 2: Haematology, Microbiology, Pathology techniques",
      "Yr 3: Advanced Diagnostics, Blood Banking, Quality Control",
      "Internship: 6 months in an approved lab / hospital",
    ],
    careers: ["Medical Lab Technologist", "Pathology Lab Technician", "Blood Bank Technician", "Biochemistry Analyst", "Research Assistant"],
    recruiters: ["Dr. Lal PathLabs", "SRL Diagnostics", "Metropolis Healthcare", "Fortis Labs", "Max Healthcare", "Thyrocare"],
    tags: ["Healthcare"],
  },

  /* ============ HOTEL MANAGEMENT ============ */
  {
    slug: "bhmct",
    name: "Bachelor of Hotel Management & Catering Technology (BHMCT)",
    short: "BHMCT",
    discipline: "Hotel Management",
    degree: "Bachelor's Degree",
    duration: "4 Years (8 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 40,
    fee: "₹80,000 / year (approx)",
    approvedBy: "AICTE, New Delhi · Affiliated to IKGPTU",
    eligibility: "10+2 in any stream with minimum 50% marks (45% reserved).",
    highlights: ["Fully equipped culinary, bakery & housekeeping labs", "Industrial exposure in 5-star hotels", "International placements possible"],
    semesters: ["Yr 1: Food Production, Housekeeping, F&B Service", "Yr 2-3: Front Office, Bakery, Wines, Nutrition", "Yr 4: Industrial Exposure + Specialisation"],
    careers: CAREER_MAP.hotel,
    recruiters: RECRUITERS_BY_STREAM.hotel,
    tags: [],
  },

  /* ============ APPLIED SCIENCES ============ */
  {
    slug: "msc-physics",
    name: "M.Sc in Physics",
    short: "M.Sc Physics",
    discipline: "Applied Sciences",
    degree: "Master's Degree",
    duration: "2 Years (4 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 30,
    fee: "₹32,000 / year (approx)",
    approvedBy: "Affiliated to Panjab University",
    eligibility: "B.Sc (Physics / Physics-Hons) with minimum 50% marks (45% reserved).",
    highlights: ["Well-equipped optics, electronics & modern-physics labs", "Preparation for CSIR-NET / GATE / JEST", "Pathway to PhD"],
    semesters: ["Sem 1-2: Classical Mechanics, EM Theory, QM, Statistical Mech.", "Sem 3-4: Solid State Physics, Electronics, Electives, Project"],
    careers: CAREER_MAP.applied,
    recruiters: ["Research Institutes", "Schools / Colleges", "Semiconductor Labs"],
    tags: ["Postgraduate"],
  },
  {
    slug: "msc-chemistry",
    name: "M.Sc in Chemistry",
    short: "M.Sc Chemistry",
    discipline: "Applied Sciences",
    degree: "Master's Degree",
    duration: "2 Years (4 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 30,
    fee: "₹32,000 / year (approx)",
    approvedBy: "Affiliated to Panjab University",
    eligibility: "B.Sc (Chemistry / Chemistry-Hons) with minimum 50% marks (45% reserved).",
    highlights: ["Modern instrumentation labs — UV, IR, NMR", "CSIR-NET / GATE prep", "Placements in Pharma / Chemical / R&D industry"],
    semesters: ["Sem 1-2: Inorganic, Organic, Physical, Analytical Chemistry", "Sem 3-4: Electives + Dissertation"],
    careers: ["Chemist", "Quality Analyst", "R&D Scientist", "Academician"],
    recruiters: RECRUITERS_BY_STREAM.pharma,
    tags: ["Postgraduate"],
  },
  {
    slug: "msc-math",
    name: "M.Sc in Mathematics",
    short: "M.Sc Mathematics",
    discipline: "Applied Sciences",
    degree: "Master's Degree",
    duration: "2 Years (4 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 30,
    fee: "₹32,000 / year (approx)",
    approvedBy: "Affiliated to Panjab University",
    eligibility: "B.Sc / B.A. with Mathematics as a subject, minimum 50% marks (45% reserved).",
    highlights: ["Focus on Analysis, Algebra, Topology, ODE/PDE", "CSIR-NET / GATE / JAM preparation", "Pathway to Data Science / Analytics"],
    semesters: ["Sem 1-2: Real Analysis, Algebra, Topology", "Sem 3-4: Numerical Methods, Electives, Dissertation"],
    careers: ["Data Analyst", "Actuarial Analyst", "Academician", "Research Scholar"],
    recruiters: ["EdTech companies", "Banks / NBFCs", "Analytics firms"],
    tags: ["Postgraduate"],
  },

  /* ============ DIPLOMA / POLYTECHNIC ============ */
  {
    slug: "diploma-civil",
    name: "Diploma in Civil Engineering",
    short: "Diploma Civil",
    discipline: "Diploma / Polytechnic",
    degree: "Diploma",
    duration: "3 Years (6 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹32,000 / year (approx)",
    approvedBy: "PSBTE Punjab · AICTE, New Delhi",
    eligibility: "10th (Matric) pass with minimum 45% marks (40% reserved).",
    highlights: ["Direct lateral entry to B.Tech 2nd year", "Surveying, CAD & construction labs", "Placements in construction industry"],
    semesters: ["Yr 1: Applied Maths, Engg Drawing, Building Materials", "Yr 2: Surveying, RCC, Estimation", "Yr 3: Project, Site Training"],
    careers: CAREER_MAP.diploma,
    recruiters: ["L&T", "Shapoorji", "PWD", "NHAI", "Local construction firms"],
    tags: ["Diploma"],
  },
  {
    slug: "diploma-mech",
    name: "Diploma in Mechanical Engineering",
    short: "Diploma Mechanical",
    discipline: "Diploma / Polytechnic",
    degree: "Diploma",
    duration: "3 Years (6 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹32,000 / year (approx)",
    approvedBy: "PSBTE Punjab · AICTE, New Delhi",
    eligibility: "10th (Matric) pass with minimum 45% marks (40% reserved).",
    highlights: ["Fitters, welding, CNC & 3D-printing workshops", "Lateral entry to B.Tech Mechanical", "Placements in manufacturing industry"],
    semesters: ["Yr 1: Engg Drawing, Workshop, Applied Maths", "Yr 2: Thermodynamics, Machines, Production", "Yr 3: Project + Industry Training"],
    careers: CAREER_MAP.diploma,
    recruiters: RECRUITERS_BY_STREAM.core,
    tags: ["Diploma"],
  },
  {
    slug: "diploma-cse",
    name: "Diploma in Computer Science & Engineering",
    short: "Diploma CSE",
    discipline: "Diploma / Polytechnic",
    degree: "Diploma",
    duration: "3 Years (6 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹32,000 / year (approx)",
    approvedBy: "PSBTE Punjab · AICTE, New Delhi",
    eligibility: "10th (Matric) pass with minimum 45% marks (40% reserved).",
    highlights: ["Programming, DBMS, Web basics", "Lateral entry to B.Tech CSE", "Local IT placements"],
    semesters: ["Yr 1: Programming, Digital Logic", "Yr 2: DBMS, OS, Web", "Yr 3: Networking, Project"],
    careers: [...CAREER_MAP.it, ...CAREER_MAP.diploma],
    recruiters: RECRUITERS_BY_STREAM.tech,
    tags: ["Diploma"],
  },
  {
    slug: "diploma-ece",
    name: "Diploma in Electronics & Communication Engineering",
    short: "Diploma ECE",
    discipline: "Diploma / Polytechnic",
    degree: "Diploma",
    duration: "3 Years (6 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹32,000 / year (approx)",
    approvedBy: "PSBTE Punjab · AICTE, New Delhi",
    eligibility: "10th (Matric) pass with minimum 45% marks (40% reserved).",
    highlights: ["Electronics, Communication & Microcontroller labs", "Lateral entry to B.Tech ECE", "Placements in electronics industry"],
    semesters: ["Yr 1: Basic Electronics, Applied Maths", "Yr 2: Comm. Systems, Microcontrollers", "Yr 3: Embedded Systems, Project"],
    careers: CAREER_MAP.diploma,
    recruiters: ["Bharat Electronics", "Local electronics MSMEs"],
    tags: ["Diploma"],
  },
  {
    slug: "diploma-electrical",
    name: "Diploma in Electrical Engineering",
    short: "Diploma Electrical",
    discipline: "Diploma / Polytechnic",
    degree: "Diploma",
    duration: "3 Years (6 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹32,000 / year (approx)",
    approvedBy: "PSBTE Punjab · AICTE, New Delhi",
    eligibility: "10th (Matric) pass with minimum 45% marks (40% reserved).",
    highlights: ["Wiring, Machines, Substation labs", "Lateral entry to B.Tech EE", "Placements in power sector"],
    semesters: ["Yr 1: Basic Electrical, Circuits, Drawing", "Yr 2: Machines, Power Systems", "Yr 3: Utilization, Project"],
    careers: CAREER_MAP.diploma,
    recruiters: ["PSPCL", "BHEL", "Local electrical firms"],
    tags: ["Diploma"],
  },
  {
    slug: "diploma-automobile",
    name: "Diploma in Automobile Engineering",
    short: "Diploma Automobile",
    discipline: "Diploma / Polytechnic",
    degree: "Diploma",
    duration: "3 Years (6 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 40,
    fee: "₹32,000 / year (approx)",
    approvedBy: "PSBTE Punjab · AICTE, New Delhi",
    eligibility: "10th (Matric) pass with minimum 45% marks (40% reserved).",
    highlights: ["Auto workshop, EV lab, Chassis lab", "Placements at Hero MotoCorp, TVS, Mahindra", "Lateral entry to B.Tech Mechanical / Auto"],
    semesters: ["Yr 1: Auto Engg Basics, Workshop", "Yr 2: IC Engines, Chassis, Auto Electricals", "Yr 3: EV / Hybrid, Project"],
    careers: [...CAREER_MAP.diploma, "Service Engineer", "EV Technician"],
    recruiters: ["Hero MotoCorp", "TVS", "Mahindra", "Maruti"],
    tags: ["Diploma"],
  },
  {
    slug: "diploma-architecture",
    name: "Diploma in Architectural Assistantship",
    short: "Diploma Architectural Assistantship",
    discipline: "Diploma / Polytechnic",
    degree: "Diploma",
    duration: "3 Years (6 Semesters)",
    mode: "Full-Time · On-Campus",
    seats: 30,
    fee: "₹32,000 / year (approx)",
    approvedBy: "PSBTE Punjab · AICTE, New Delhi",
    eligibility: "10th (Matric) pass with minimum 45% marks (40% reserved). Aptitude in drawing preferred.",
    highlights: ["AutoCAD, Revit, SketchUp & Studio labs", "Career as Architect Assistant / Interior Designer", "Pathway to B.Arch (through NATA)"],
    semesters: ["Yr 1: Building Materials, Drawing, Design Basics", "Yr 2: Working Drawings, Interior Design", "Yr 3: Studio + Project"],
    careers: ["Architect Assistant", "Interior Designer", "Draftsman", "CAD Designer"],
    recruiters: ["Architecture firms", "Interior-design studios", "Real-estate developers"],
    tags: ["Diploma", "Design"],
  },
  {
    slug: "diploma-pharmacy",
    name: "Diploma in Pharmacy",
    short: "Diploma Pharmacy",
    discipline: "Diploma / Polytechnic",
    degree: "Diploma",
    duration: "2 Years",
    mode: "Full-Time · On-Campus",
    seats: 60,
    fee: "₹50,000 / year (approx)",
    approvedBy: "Pharmacy Council of India (PCI) · PSBTE",
    eligibility: "10+2 with Physics, Chemistry and Biology / Maths with minimum 50% marks (45% reserved).",
    highlights: ["Direct pathway to registered Pharmacist licence", "Dispensing & compounding labs", "Retail / hospital pharmacy careers"],
    semesters: ["Yr 1: Pharmaceutics, Human Anatomy, Pharm. Chemistry", "Yr 2: Pharmacology, Pharmacognosy, Training"],
    careers: CAREER_MAP.pharma,
    recruiters: RECRUITERS_BY_STREAM.pharma,
    tags: ["Diploma"],
  },
];

/* Helpers */
export const getProgramBySlug = (slug) => programs.find((p) => p.slug === slug);

export const disciplineOrder = [
  "Engineering",
  "Management",
  "Computing / IT",
  "Pharmacy",
  "Allied Health Sciences",
  "Education",
  "Agriculture",
  "Hotel Management",
  "Applied Sciences",
  "Diploma / Polytechnic",
];

/* ============ RICH CONTENT HELPERS ============ */
/**
 * Discipline-level defaults so every program gets landing-page-quality
 * content without hand-authoring 37 unique blocks.
 */
const KPIS_BY_DISCIPLINE = {
  "Engineering":            { highPackage: "₹24.5 LPA", avgPackage: "₹5.8 LPA",  placementRate: "92%", recruitersCount: "200+" },
  "Management":             { highPackage: "₹18.0 LPA", avgPackage: "₹6.2 LPA",  placementRate: "94%", recruitersCount: "180+" },
  "Computing / IT":         { highPackage: "₹24.5 LPA", avgPackage: "₹5.5 LPA",  placementRate: "93%", recruitersCount: "200+" },
  "Pharmacy":               { highPackage: "₹9.2 LPA",  avgPackage: "₹3.8 LPA",  placementRate: "89%", recruitersCount: "120+" },
  "Allied Health Sciences": { highPackage: "₹8.5 LPA",  avgPackage: "₹3.6 LPA",  placementRate: "90%", recruitersCount: "100+" },
  "Education":              { highPackage: "₹7.5 LPA",  avgPackage: "₹3.6 LPA",  placementRate: "90%", recruitersCount: "80+"  },
  "Agriculture":            { highPackage: "₹8.4 LPA",  avgPackage: "₹4.2 LPA",  placementRate: "88%", recruitersCount: "60+"  },
  "Hotel Management":       { highPackage: "₹9.6 LPA",  avgPackage: "₹4.4 LPA",  placementRate: "91%", recruitersCount: "70+"  },
  "Applied Sciences":       { highPackage: "₹8.0 LPA",  avgPackage: "₹4.0 LPA",  placementRate: "86%", recruitersCount: "60+"  },
  "Diploma / Polytechnic":  { highPackage: "₹6.4 LPA",  avgPackage: "₹3.2 LPA",  placementRate: "85%", recruitersCount: "150+" },
};

const SKILLS_BY_DISCIPLINE = {
  "Engineering":       ["Analytical thinking", "Design & simulation", "Project management", "Problem solving", "Teamwork", "Industry-grade tools", "Prototyping", "Communication"],
  "Management":        ["Leadership", "Strategic thinking", "Data-driven decisions", "Communication", "Negotiation", "Financial acumen", "Marketing insight", "Team building"],
  "Computing / IT":    ["Programming", "System design", "Databases", "Cloud & DevOps", "AI/ML basics", "Cyber security", "Version control (Git)", "Agile methodologies"],
  "Pharmacy":          ["Drug formulation", "Pharmacology", "Regulatory compliance", "Clinical research", "Lab techniques", "Patient counselling", "Quality assurance"],
  "Allied Health Sciences": ["Clinical instrumentation", "Patient handling", "Sterilisation protocols", "Diagnostic imaging", "Lab safety", "Medical documentation"],
  "Education":         ["Pedagogy design", "Classroom management", "Assessment techniques", "ICT in teaching", "Child psychology", "Curriculum planning"],
  "Agriculture":       ["Crop management", "Soil science", "Agri-tech tools", "Farm economics", "Extension work", "Sustainable practices"],
  "Hotel Management":  ["Guest handling", "F&B operations", "Culinary skills", "Front-office management", "Event planning", "Multicultural communication"],
  "Applied Sciences":  ["Scientific method", "Data analysis", "Instrumentation", "Research writing", "Critical thinking", "Lab safety"],
  "Diploma / Polytechnic": ["Hands-on technical skills", "Site / workshop safety", "CAD / drafting", "Machinery operation", "Troubleshooting", "Blueprint reading"],
};

const WHY_SSGI_BY_DISCIPLINE = {
  "Engineering": [
    { title: "IBM CoE integration",     copy: "Curriculum aligned with IBM Centre of Excellence and Pearson VUE certifications." },
    { title: "Industry-grade labs",     copy: "Modern labs equipped with the same tools used in leading MNCs and R&D houses." },
    { title: "Career Udaan PAP",        copy: "Placement Assurance Programme runs from Year 1 with aptitude, mock interviews & industry mentors." },
    { title: "Research & patents",      copy: "Active R&D cell, patents and publications by faculty and students every year." },
  ],
  "Management": [
    { title: "Live industry projects",  copy: "Every semester includes real business case studies and consulting assignments with local & national firms." },
    { title: "Leadership network",      copy: "Guest lectures by CEOs, founders and senior leaders across marketing, finance, HR & operations." },
    { title: "Dual specialisation",     copy: "Choose two specialisations to become a well-rounded, career-flexible manager." },
    { title: "Placement drives",        copy: "180+ recruiters visit every year — banking, retail, FMCG, consulting, IT & startups." },
  ],
  "Computing / IT": [
    { title: "IBM & Pearson tracks",    copy: "Industry-integrated tracks in AI/ML, Cloud, Cyber Security, Full-Stack & Data Science." },
    { title: "Hackathon culture",       copy: "Active coding, hackathon, GitHub & open-source clubs on campus." },
    { title: "Global certifications",   copy: "Pearson VUE testing centre on campus for AWS, Azure, Google, Microsoft & CompTIA." },
    { title: "Highest ROI",             copy: "Consistently among SSGI's top-placed streams — up to ₹24.5 LPA offers." },
  ],
  "Pharmacy": [
    { title: "PCI-approved curriculum", copy: "All programmes are PCI-approved and eligible for Pharmacist registration." },
    { title: "Well-equipped labs",      copy: "Pharmaceutics, Pharmacology, Pharm. Chemistry, Pharmacognosy — all industry-grade." },
    { title: "GPAT / NIPER prep",       copy: "Faculty-led coaching for GPAT and NIPER JEE alongside regular curriculum." },
    { title: "Industry MoUs",           copy: "Partnerships with Cipla, Sun Pharma, Alkem, Zydus & more for training & placements." },
  ],
  "Allied Health Sciences": [
    { title: "Hospital-integrated training", copy: "Clinical postings at leading hospitals and diagnostic labs during the course." },
    { title: "Modern instrumentation",       copy: "X-Ray, CT, MRI, USG, biochemistry analysers, cell counters and pathology labs on campus." },
    { title: "Mandatory internship",         copy: "6-month structured internship makes you fully job-ready before graduation." },
    { title: "In-demand roles",              copy: "Healthcare workforce is one of India's fastest-growing sectors with strong placements." },
  ],
  "Education": [
    { title: "NCTE-approved",           copy: "Programmes fully approved by the National Council for Teacher Education (NCTE)." },
    { title: "Practicum in real schools", copy: "Extensive teaching practice in reputed CBSE / ICSE & Punjab-Board schools." },
    { title: "CTET / STET prep",        copy: "Structured preparation for CTET Paper 1 & 2 alongside your degree." },
    { title: "Modern ed-tech lab",      copy: "ICT and ed-tech lab to prepare you for the modern digital classroom." },
  ],
  "Agriculture": [
    { title: "ICAR-aligned",            copy: "Curriculum aligned to ICAR framework with Rural Agri Work Experience (RAWE)." },
    { title: "Field & farm exposure",   copy: "Regular visits to KVKs, agri-research stations & progressive farmers' farms." },
    { title: "Agri-preneurship push",   copy: "Support for agri-startups through incubation, mentorship & funding help." },
    { title: "Govt. exam readiness",    copy: "Prep support for IBPS-AFO, NABARD Grade A, ICAR ARS and State Agri exams." },
  ],
  "Hotel Management": [
    { title: "5-star industry training", copy: "Industrial exposure with Taj, Oberoi, Marriott, Radisson & other premium hotels." },
    { title: "Culinary & bakery labs",  copy: "Fully equipped training kitchens, bakery and mocktail lab on campus." },
    { title: "International placements", copy: "Cruise-line and international hospitality placement opportunities." },
    { title: "Personality & grooming",  copy: "Structured grooming, communication & guest-handling modules." },
  ],
  "Applied Sciences": [
    { title: "Research-first culture",  copy: "Student-led research projects, publications and paper presentations." },
    { title: "CSIR-NET / GATE / JAM prep", copy: "Faculty-led coaching for national-level entrance exams." },
    { title: "Advanced instrumentation", copy: "Access to modern optics, spectroscopy & analytical instruments." },
    { title: "Pathway to PhD",          copy: "Strong pathway to PhD via UGC-NET, GATE, JEST and international programmes." },
  ],
  "Diploma / Polytechnic": [
    { title: "Lateral entry to B.Tech", copy: "Direct entry to B.Tech 2nd year — a fast track to full engineering degree." },
    { title: "Industry-grade workshops", copy: "CNC, welding, PCB, 3D-printing, CAD and simulation labs on campus." },
    { title: "PSBTE + AICTE approved",  copy: "Programmes approved by PSBTE Punjab and AICTE, New Delhi." },
    { title: "Faster career start",     copy: "Get job-ready in 3 years for technician / junior engineer / CAD roles." },
  ],
};

const FAQS_GENERIC = [
  {
    q: "How can I apply for this program?",
    a: "Just fill the application form on our Apply page — it takes under 60 seconds. Our admissions counsellor will call you within 24 hours with the next steps (documents, seat booking, fee payment).",
  },
  {
    q: "Are scholarships available?",
    a: "Yes. Merit scholarships up to 100% tuition waiver, sports & NCC scholarships, sibling / alumni referral discounts, and government schemes (PMSSS, SC/ST, EWS, OBC) — all applicable subject to eligibility. See our Apply page for the full slab list.",
  },
  {
    q: "Is hostel accommodation available?",
    a: "Yes. Separate, fully-secured hostels for boys and girls are available on campus with Wi-Fi, mess, laundry, gym and 24×7 warden support. Hostel is optional and priced separately from tuition.",
  },
  {
    q: "Are EMI / education-loan options available for fees?",
    a: "Yes. We have partnerships with leading NBFCs and PSU banks for education-loan and 0% EMI schemes. Our finance help-desk assists with documentation and end-to-end processing.",
  },
  {
    q: "What is the fee refund policy?",
    a: "SSGI follows the AICTE / UGC-prescribed refund policy. Withdrawals before class commencement receive a refund minus a nominal processing fee, processed within 15 working days.",
  },
];

const FAQS_BY_DISCIPLINE = {
  "Engineering": [
    { q: "Do I need a JEE Main score to apply?", a: "JEE Main is preferred but not mandatory for management-quota seats. Direct admissions are open on 10+2 (PCM) merit as per IKGPTU norms." },
    { q: "Which B.Tech branch has the highest placements?", a: "B.Tech CSE and B.Tech ECE consistently see the highest placement packages (up to ₹24.5 LPA) at SSGI." },
  ],
  "Management": [
    { q: "Do I need CAT / MAT / CMAT for MBA?", a: "Any valid CAT / MAT / CMAT / State CET score is accepted. Direct admissions are open on graduation-merit basis as per IKGPTU norms." },
    { q: "What specialisations are offered in MBA?", a: "Marketing, Finance, HR, Operations, IT, and Business Analytics — students choose two dual-specialisations in the 2nd year." },
  ],
  "Computing / IT": [
    { q: "Is the curriculum aligned with industry certifications?", a: "Yes — we integrate IBM CoE, Pearson VUE, AWS, Azure, Google Cloud and Microsoft certification tracks alongside the university curriculum." },
    { q: "Which languages / tools are taught?", a: "Python, Java, JavaScript, C/C++, SQL, HTML/CSS, React, Node.js, Git, Docker, plus AI/ML frameworks and cloud tools." },
  ],
  "Pharmacy": [
    { q: "Is this a PCI-approved programme?", a: "Yes — all our pharmacy programmes are approved by the Pharmacy Council of India (PCI) and eligible for pharmacist registration." },
    { q: "Do you support GPAT / NIPER preparation?", a: "Yes — dedicated faculty-led GPAT and NIPER-JEE coaching is included as part of the programme." },
  ],
  "Education": [
    { q: "Is this programme NCTE-approved?", a: "Yes — all our education programmes are approved by the National Council for Teacher Education (NCTE)." },
    { q: "Will I be prepared for CTET / STET?", a: "Yes — structured CTET / STET preparation runs alongside your regular coursework." },
  ],
  "Agriculture": [
    { q: "Is your B.Sc Agriculture programme ICAR-aligned?", a: "Yes — the curriculum follows the ICAR framework and includes Rural Agri Work Experience (RAWE)." },
    { q: "Which government exams can this prepare me for?", a: "IBPS-AFO, NABARD Grade A, ICAR ARS, and various State Agriculture Officer / Extension Officer exams." },
  ],
  "Hotel Management": [
    { q: "Do I need any prior hospitality experience?", a: "No — we start from the fundamentals. Any 10+2 student with a passion for hospitality can join." },
    { q: "Are there international placement opportunities?", a: "Yes — SSGI has tie-ups with cruise-lines and international hotel chains for global placements." },
  ],
  "Applied Sciences": [
    { q: "Does the programme prepare me for CSIR-NET / GATE?", a: "Yes — faculty-led coaching and mock tests for CSIR-NET, GATE and JAM are integrated with the curriculum." },
    { q: "Can I go into a PhD after this?", a: "Absolutely — many of our M.Sc alumni have moved into PhD programmes at IITs, IISc, and reputed foreign universities." },
  ],
  "Diploma / Polytechnic": [
    { q: "Can I pursue B.Tech after this diploma?", a: "Yes — you get lateral entry directly into 2nd year of B.Tech via the LEET route." },
    { q: "How soon can I start working?", a: "Right after your 3-year diploma. Many students take up junior engineer / technician / CAD-assistant roles or continue with lateral B.Tech." },
  ],
};

export const getKpis = (program) => KPIS_BY_DISCIPLINE[program.discipline] || KPIS_BY_DISCIPLINE["Engineering"];
export const getSkills = (program) => SKILLS_BY_DISCIPLINE[program.discipline] || SKILLS_BY_DISCIPLINE["Engineering"];
export const getWhySSGI = (program) => WHY_SSGI_BY_DISCIPLINE[program.discipline] || WHY_SSGI_BY_DISCIPLINE["Engineering"];
export const getFaqs = (program) => [
  ...(FAQS_BY_DISCIPLINE[program.discipline] || []),
  ...FAQS_GENERIC,
];

/**
 * "Learning outcomes" – succinct promises the program delivers.
 * We derive from highlights + skills so no per-program authoring is needed.
 */
export const getLearningOutcomes = (program) => {
  const skills = getSkills(program);
  return [
    `Master the core technical foundations of ${program.short}.`,
    `Build a professional portfolio through capstone projects and real-world case studies.`,
    `Develop industry-relevant skills: ${skills.slice(0, 4).join(", ")}.`,
    `Prepare for national exams, higher studies or immediate placement in ${program.discipline}.`,
    `Graduate with confidence, communication and leadership skills for a global career.`,
  ];
};

/**
 * Fee breakdown table.
 */
export const getFeeBreakdown = (program) => {
  const tuition = program.fee.replace("(approx)", "").trim();
  return [
    { head: "Tuition Fee", value: tuition, note: "Payable per academic year" },
    { head: "Registration Fee", value: "₹5,000", note: "One-time, at admission" },
    { head: "Examination Fee", value: "As per University", note: "Paid to IKGPTU / Panjab Univ." },
    { head: "Hostel (Optional)", value: "₹65,000 / year", note: "Includes accommodation + mess" },
    { head: "Transport (Optional)", value: "₹22,000 – ₹35,000", note: "Distance-based slabs" },
  ];
};

